import { useCallback } from "react";
import { SdbProject } from "@custom-types/project-types";
import { PhotogrammetryUploadTaskContext, UploadElementType } from "@custom-types/file-upload-types";
import { useCheckFilesErrorToast } from "@hooks/data-management/use-check-files-error-toast";
import { haveFileNamesValidLength } from "@hooks/upload-tasks/upload-tasks-utils";
import {
  ALLOWED_EXTENSIONS_ALL,
  ALLOWED_EXTENSIONS_ORBIS,
  ALLOWED_EXTENSIONS_PHOTOGRAMMETRY,
  PHOTOGRAMMETRY_MAX_FILE_SIZE_IN_MB,
  SCAN_MAX_FILE_SIZE_IN_MB,
  filesInfoForTracking,
  getImportFolderName,
  getScansAlreadyUploaded,
  isGeoslam,
  isGLS,
  isIndexV2,
  isSupportedFocusScan,
  isValidGlsFile,
  isValidGeoslamFile,
  isPhotogrammetryImage,
} from "@pages/data-management/import-data/import-data-utils";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { isAlphaTestingEnabledSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { FILE_SIZE_MULTIPLIER, getFilesWithDuplicateNames, isValidFileExtension, sortFiles } from "@utils/file-utils";
import { assert, generateGUID } from "@faro-lotv/foundation";
import { validateFile } from "@hooks/file-upload-utils";
import { useStagingAreaApiClient } from "@api/stagingarea-api/use-stagingarea-api-client";
import { getLsDataV2Package, getScanByFilename, StagingAreaApiClient } from "@api/stagingarea-api/stagingarea-api";
import {
  AnyFilesInfo,
  LsDataV2FilesInfo,
  BlinkFilesInfo,
  DataManagementUploadElementType,
  OrbisFilesInfo,
  FocusFilesInfo,
  CheckFilesErrorToastType,
  InvalidFilesInfo,
  isInvalidFileInfo,
  isPhotogrammetryFileInfo,
  PhotogrammetryFilesInfo,
  UploadedIdsMap,
  isFocusFileInfo,
  isOrbisFileInfo,
} from "@pages/data-management/data-management-types";
import { useCanUsePhotogrammetry } from "@pages/data-management/data-management-utils";
import { FileExt } from "@utils/file-ext";
import { FocusZipFile, createFocusDummyLsDataV2, getFocusZipFile } from "@pages/data-management/import-data/focus-file";
import { ZipFile } from "@utils/zip-file";
import { extractUuidsFromFile, createOrbisDummyLsDataV2, OrbisUuids } from "@pages/data-management/import-data/orbis-file";
import { ProjectId } from "@custom-types/base-types";

export type ReturnFunctionOnCheckFiles = (
  selectedFiles: FileList | (File | FileExt)[],
  /**
   * A map of the externalIds of the uploaded ELS scans, as returned by getUploadedIdsMap.
   * Used by the useOnCheckFiles hook.
   */
  uploadedIdsMap: UploadedIdsMap,
) => Promise<AnyFilesInfo[]>;

/**
 * Hook for file selection and validation for both Blink and Photogrammetry files.
 * Returns an info object with the data required by the associated upload hook, e.g. useOnUploadBlinkFiles,
 * and false if some kind of error happened.
 * @param project The project to upload the files to.
 */
export function useOnCheckFiles(project: SdbProject): ReturnFunctionOnCheckFiles {
  const { trackEvent } = useTrackEvent();
  const checkFilesErrorToast = useCheckFilesErrorToast();
  const stagingAreaApiClient = useStagingAreaApiClient({ projectId: project.id });
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const isAlphaTestingEnabled = useAppSelector(isAlphaTestingEnabledSelector);
  const canUsePhotogrammetry = useCanUsePhotogrammetry();

  return useCallback(
    async (selectedFiles, uploadedIdsMap): Promise<AnyFilesInfo[]> => {
      selectedFiles = [...selectedFiles];

      // We want to track the action of selecting the files, so it's better to track before any validation.
      // If/when the upload actually starts, we track another event: DataManagementEvents.startUpload.
      trackEvent({
        name: DataManagementEvents.selectFiles,
        props: filesInfoForTracking(selectedFiles),
      });

      try {
        if (!selectedFiles.length) {
          checkFilesErrorToast(CheckFilesErrorToastType.noFiles);
          return [];
        }

        // Validate file/folder names. If they're too long it can lead to an error or unexpected behavior.
        if (!haveFileNamesValidLength(selectedFiles)) {
          checkFilesErrorToast(CheckFilesErrorToastType.tooLongFilename);
          return [];
        }

        // JPEGs are checked in getPhotogrammetryFilesInfo, since the LsDataV2 folder might contain unused files, which should
        // not cause errors.
        const invalidZeroBytesFiles = selectedFiles.filter((file) =>
          (isGLS(file.name) || isGeoslam(file.name)) && file.size === 0
        );
        if (invalidZeroBytesFiles.length > 0) {
          checkFilesErrorToast(
            CheckFilesErrorToastType.zeroBytesFiles, undefined, undefined, invalidZeroBytesFiles.map((file) => file.name)
          );
          return [];
        }

        const fileInfos = await extractFilesInfos(selectedFiles, project.id, uploadedIdsMap, stagingAreaApiClient);
        if (fileInfos.length === 0) {
          checkFilesErrorToast(CheckFilesErrorToastType.noFiles);
          return [];
        }

        // Check access to Photogrammetry feature.
        if (!canUsePhotogrammetry) {
          for (const info of fileInfos) {
            if (isPhotogrammetryFileInfo(info)) {
              info.errorType = CheckFilesErrorToastType.noPhotogrammetryAccess;
            }
          }
        }

        // Check access to Focus feature.
        if (!isDevModeEnabled) {
          for (const info of fileInfos) {
            if (isFocusFileInfo(info)) {
              info.errorType = CheckFilesErrorToastType.noFocusAccess;
            }
          }
        }

        // Check access to single Orbis upload (dev feature)
        if (!isDevModeEnabled) {
          for (const info of fileInfos) {
            if (isOrbisFileInfo(info) && info.hasDummyLsDataV2) {
              info.errorType = CheckFilesErrorToastType.noOrbisAccess;
            }
          }
        }

        // Check access to Orbis feature.
        if (!isAlphaTestingEnabled) {
          for (const info of fileInfos) {
            if (isOrbisFileInfo(info) && !info.hasDummyLsDataV2) {
              info.errorType = CheckFilesErrorToastType.noOrbisAccess;
            }
          }
        }

        // Show Toasts for invalid files/folders
        const fileInfosInvalid = fileInfos.filter(isInvalidFileInfo);
        for (const info of fileInfosInvalid) {
          // Show folder name if multiple folders were dragged.
          checkFilesErrorToast(info.errorType, info.folderName, info.projectName, info.invalidFileName);
        }

        // TODO(TF-2044): Don't filter out invalid data sets, but show them with error message in the table.
        // return fileInfos;
        return fileInfos.filter((info) => !isInvalidFileInfo(info));
      } catch (error) {
        // In Chrome, a `NotFoundError` is thrown when trying to collect files from a folder with too long name.
        // Maybe the same error could occur when dragging a folder and then very quickly deleting some files from it.
        // In Firefox, collecting files from such a folder doesn't throw an Error, may instead miss some files.
        if ((error as Error)?.name === "NotFoundError") {
          checkFilesErrorToast(CheckFilesErrorToastType.tooLongFilename);
        } else {
          checkFilesErrorToast(CheckFilesErrorToastType.collectFiles);
        }
        return [];
      }
    },
    [
      canUsePhotogrammetry,
      checkFilesErrorToast,
      isAlphaTestingEnabled,
      isDevModeEnabled,
      project.id,
      stagingAreaApiClient,
      trackEvent,
    ]
  );
}

/**
 * Extract importable data sets from the selected files.
 * @param files Files, if possible with webkitRelativePath, so we can use the folder structure.
 * @param projectId
 * @param uploadedIdsMap Set of already uploaded external scan IDs.
 * @param stagingAreaApiClient
 * @returns List of FilesInfo objects, one for each valid folder. May include InvalidFilesInfo objects.
 */
export async function extractFilesInfos(
  files: (File | FileExt)[],
  projectId: ProjectId,
  uploadedIdsMap: UploadedIdsMap,
  stagingAreaApiClient: StagingAreaApiClient
): Promise<AnyFilesInfo[]> {
  if (files.length === 0) {
    return [];
  }

  // Copy, so we don't accidentally modify the input array.
  files = [...files];

  // On macOS, Finder creates a hidden file ".DS_Store" in every folder.
  // This file is not a valid file, and we don't want to upload it.
  files = files.filter((file) => file.name !== ".DS_Store");

  // Without `webkitRelativePath` support, we can't decide which folder a file belongs to.
  // The user will be able to drag only a single Blink dataset at once, or he will get the `noMultiFolder` error.
  if (!files.every((file) => !!file.webkitRelativePath)) {
    const indexV2s = files.filter((file) => isIndexV2(file.name));
    const focusVersionFiles = files.filter((file) => file.name === "Version");
    let focusVersionFile: File | undefined;
    for (const file of focusVersionFiles) {
      if (await isSupportedFocusScan(file)) {
        focusVersionFile = file;
        break;
      }
    }
    if (indexV2s.length > 0) {
      return await getLsDataV2FilesInfo(files, uploadedIdsMap, stagingAreaApiClient);
    } else if (focusVersionFile) {
      // Not supported for Focus scans, as we need the folder structure.
      return [];
    } else if (files.some((file) => isPhotogrammetryImage(file.name))) {
      return [getPhotogrammetryFilesInfo(files, projectId)];
    } else if (files.some((file) => isValidFileExtension(file, ALLOWED_EXTENSIONS_ORBIS))) {
      return await getOrbisFilesInfo(files);
    } else {
      return [];
    }
  }

  // We have webkitRelativePath support, so we can use the folder structure.
  const filesInfos: AnyFilesInfo[] = [];

  // Add LSDataV2 datasets
  while (true) {
    const indexV2 = files.find((file) => isIndexV2(file.name));
    if (!indexV2) {
      break;
    }

    const folderPrefix = getFolderPrefix(indexV2.webkitRelativePath);
    const folderFiles = files.filter((file) => file.webkitRelativePath.startsWith(folderPrefix));
    const oldLength = files.length;
    files = files.filter((file) => !file.webkitRelativePath.startsWith(folderPrefix));
    assert(folderFiles.length > 0 && files.length < oldLength, "Logic error: No matching files found for the folder.");

    filesInfos.push(... await getLsDataV2FilesInfo(folderFiles, uploadedIdsMap, stagingAreaApiClient));
  }

  // Find all focus files
  const { remainingFiles, focusFiles } = await findFocusFiles(files);

  if (focusFiles.length > 0) {
    // Create a Focus Dataset
    filesInfos.push(getFocusFilesInfo(focusFiles));
  }
  files = remainingFiles;

  // Add Orbis datasets
  if (files.some((file) => isValidFileExtension(file, ALLOWED_EXTENSIONS_ORBIS))) {
    filesInfos.push(... await getOrbisFilesInfo(files));
  }
  // Add Photogrammetry datasets
  if (files.some((file) => isValidFileExtension(file, ALLOWED_EXTENSIONS_PHOTOGRAMMETRY))) {
    filesInfos.push(getPhotogrammetryFilesInfo(files, projectId));
  }

  // Sort all FileInfo objects by project or folder name
  filesInfos.sort((a, b) =>
    (a.projectName ?? a.folderName ?? "")
      .localeCompare(b.projectName ?? b.folderName ?? ""));

  return filesInfos;
}

function getFocusFilesInfo(focusFiles: FocusZipFile[]): FocusFilesInfo {
  const focusDummyLsDataV2 = createFocusDummyLsDataV2(focusFiles);
  return {
    elementType: UploadElementType.focusScan,
    lsDataV2: focusDummyLsDataV2,
    hasDummyLsDataV2: true,
    folderName: `${focusFiles.length} Focus scan${focusFiles.length > 1 ? "s" : ""}`,
    filesToUpload: focusFiles,
    scansAlreadyUploaded: new Set<File>([]),
  } satisfies FocusFilesInfo;
}

/**
 * @param webkitRelativePath E.g. "folder/subfolder/image.jpg" or "image.jpg".
 * @returns E.g. "folder/subfolder/", or empty string if path contains no folder.
 */
function getFolderPrefix(webkitRelativePath: string): string {
  const lastSlash = webkitRelativePath.lastIndexOf("/");
  return (lastSlash < 0) ? "" :
    webkitRelativePath.substring(0, lastSlash + 1);
}

/**
 * Reads the different LsDataV2 projects.
 * Supports Blink and Orbis scans.
 *
 * Returns InvalidFilesInfo for mixed projects, or if the files are invalid.
 *
 * @param files Files, which are descendants of one root folder.
 * @param uploadedIdsMap Set of already uploaded external scan IDs.
 * @param stagingAreaApiClient
 * @returns Extracted BlinkFilesInfo/OrbisFilesInfo, or InvalidFilesInfo if files are mixed/missing or invalid.
 */
async function getLsDataV2FilesInfo(
  files: (File | FileExt)[], uploadedIdsMap: UploadedIdsMap, stagingAreaApiClient: StagingAreaApiClient
): Promise<(LsDataV2FilesInfo | InvalidFilesInfo)[]> {
  const folderName = getImportFolderName(files);
  function getInvalid(
    elementType: DataManagementUploadElementType,
    errorType: CheckFilesErrorToastType,
    projectName?: string | undefined,
    invalidFileName?: string | undefined
  ): InvalidFilesInfo {
    return {
      elementType,
      folderName,
      projectName,
      filesToUpload: [],
      errorType,
      invalidFileName,
    };
  }

  // 1. Check max file size
  const allowedExtensions = ALLOWED_EXTENSIONS_ALL;
  let allowedFiles: (File | FileExt)[] = [];
  for (const file of files) {
    const result = validateFile({ file, allowedExtensions, maxFileSize: SCAN_MAX_FILE_SIZE_IN_MB });
    if (result.isValid) {
      allowedFiles.push(file);
    } else if (isGLS(file.name)) {
      // Too large *.gls files should generate a toast message.
      return [getInvalid(UploadElementType.elsScan, CheckFilesErrorToastType.tooLargeScans, undefined, file.name)];
    } else if (isGeoslam(file.name)) {
      // Too large *.geoslam files should generate a toast message.
      return [getInvalid(UploadElementType.orbisScan, CheckFilesErrorToastType.tooLargeScans, undefined, file.name)];
    } else {
      // TODO: [TF-2089] Only single file max size is checked here, not the size of the zipped folder
      // (this is not really knowable before upload).
      return [getInvalid(UploadElementType.focusScan, CheckFilesErrorToastType.tooLargeScans, undefined, file.name)];
    }
  }
  
  // 2. Collect all scan files
  const glsFiles = allowedFiles.filter((file) => isGLS(file.name));
  const geoslamFiles = allowedFiles.filter((file) => isGeoslam(file.name));
  const { remainingFiles, focusFiles } = await findFocusFiles(allowedFiles);
  allowedFiles = remainingFiles;
  
  // 3. Verify that we have at least one type of scan files to determine the upload type.
  let uploadElementType: UploadElementType | undefined = undefined;
  if (glsFiles.length > 0) {
    uploadElementType = UploadElementType.elsScan;
  } else if (geoslamFiles.length > 0) {
    uploadElementType = UploadElementType.orbisScan;
  } else if (focusFiles.length > 0) {
    uploadElementType = UploadElementType.focusScan;
  } else {
    return [getInvalid(UploadElementType.elsScan, CheckFilesErrorToastType.noFiles)];
  }

  // 4. Read LsDataV2 metadata.

  // If the browser doesn't support `webkitRelativePath` (which is widely supported), but the user drags multiple folders
  // at once, we cannot determine which files belong together.
  if (allowedFiles.filter((file) => isIndexV2(file.name)).length > 1) {
    return [getInvalid(uploadElementType, CheckFilesErrorToastType.noMultiFolder)];
  }

  const lsDataV2Files = getLsDataV2Package(allowedFiles);
  if (!lsDataV2Files?.isValid) {
    return [getInvalid(uploadElementType, CheckFilesErrorToastType.invalidMetadata)];
  }

  // Try to read the metadata, and set to undefined if it fails.
  const lsDataV2 = await stagingAreaApiClient.postReadLsDataV2(lsDataV2Files.files)
    .catch(() => undefined);
  if (!lsDataV2) {
    return [getInvalid(uploadElementType, CheckFilesErrorToastType.invalidMetadata)];
  }

  const allScanFiles = [...glsFiles, ...geoslamFiles, ...focusFiles];

  // Before the introduction of LsDataV2 projects, we supported uploading extra *.gls files
  // that were not referenced in the LsDataV2 metadata. Let's keep this behavior for now.
  // Later, it could be restricted to Dev Mode.
  const hasScanFilesWithoutProject = allScanFiles.some(
    (file) => getScanByFilename(file.name, lsDataV2)?.projectUuid === undefined
  );
  if (hasScanFilesWithoutProject) {
    lsDataV2.projects.push({
      uuid: "",
      name: folderName,
    });
  }

  // 5. For each project in lsDataV2, create a separate FileInfo object.
  const fileInfos: (LsDataV2FilesInfo | InvalidFilesInfo)[] = [];
  for (const project of lsDataV2.projects) {

    // Filter the scan files that belong to the current project.
    const projectScanFiles = allScanFiles.filter(
      (file) => (getScanByFilename(file.name, lsDataV2)?.projectUuid || "") === project.uuid
    );

    // Check validity of the scan files
    const hasInvalidGlsFile = glsFiles.some((file) => !isValidGlsFile(file, lsDataV2));
    if (hasInvalidGlsFile) {
      for (const file of glsFiles.filter((file) => isValidGlsFile(file, lsDataV2))) {
        fileInfos.push(getInvalid(uploadElementType, CheckFilesErrorToastType.invalidFormat, project.name, file.name));
        continue;
      }
    }

    const hasInvalidGeoslamFile = geoslamFiles.some((file) => !isValidGeoslamFile(file, lsDataV2));
    if (hasInvalidGeoslamFile) {
      for (const file of geoslamFiles.filter((file) => !isValidGeoslamFile(file, lsDataV2))) {
        fileInfos.push(getInvalid(uploadElementType, CheckFilesErrorToastType.invalidFormat, project.name, file.name));
        continue;
      }
    }

    // TODO [TF-2089] Verify focus files here
  
    const filesDuplicate = getFilesWithDuplicateNames(projectScanFiles);
    if (filesDuplicate.size > 0) {
      fileInfos.push(getInvalid(
        uploadElementType, CheckFilesErrorToastType.duplicateFiles, project.name, [...filesDuplicate][0].name
      ));
      continue;
    }
  
    const scansAlreadyUploaded = getScansAlreadyUploaded(projectScanFiles, lsDataV2, uploadedIdsMap);
    // If all scans were already uploaded before, we don't have anything to do.
    const hasOnlyExistingScans = projectScanFiles.every((file) => scansAlreadyUploaded.has(file));
    if (hasOnlyExistingScans) {
      fileInfos.push(getInvalid(uploadElementType, CheckFilesErrorToastType.noNewFiles, project.name));
      continue;
    }
  
    // Create the LSDatav2 zip file with, where the folder prefix is removed
    // e.g. "folder/subfolder/index-v2"          -> "index-v2"
    //      "folder/subfolder/ls-data/objects/0" -> "ls-data/objects/0"
    let lsDataV2ZipFile: ZipFile | undefined;
    const indexFile = lsDataV2Files.files.find((file: File) => file.name === "index-v2");
    if (indexFile) {
      const folderPrefix = getFolderPrefix(indexFile.webkitRelativePath);
      const { folderFilesExt } = removeFolderPrefix(lsDataV2Files.files, folderPrefix);
      lsDataV2ZipFile = new ZipFile(folderFilesExt, "lsDataV2.zip");
    }
  
    const filesSorted = sortFiles(projectScanFiles);
    const filesToUpload = filesSorted.filter((file) => !scansAlreadyUploaded.has(file));
  
    const commonProps = {
      folderName: folderName,
      projectName: project.name,
      filesToUpload,
      lsDataV2,
      lsDataV2ZipFile,
      scansAlreadyUploaded,
      hasDummyLsDataV2: false,
    };

    switch (uploadElementType) {
      case UploadElementType.elsScan:
        fileInfos.push({
          elementType: uploadElementType,
          ...commonProps,
        } satisfies BlinkFilesInfo);
        break;
  
      case UploadElementType.orbisScan:
        fileInfos.push({
          elementType: uploadElementType,
          ...commonProps,
        } satisfies OrbisFilesInfo);
        break;
  
      case UploadElementType.focusScan:
        fileInfos.push({
          elementType: uploadElementType,
          ...commonProps,
        } satisfies FocusFilesInfo);
        break;
  
      default:
        uploadElementType satisfies never;
        fileInfos.push(getInvalid(uploadElementType, CheckFilesErrorToastType.noFiles, project.name));
        break;
    }
  };

  return fileInfos;
}

/**
 * Finds Focus files in the given files array.
 * Returns the remaining files and the found FocusFiles as FocusZipFile[]
 *
 * @param files Files will be modified in place!
 *              Only the files that are not part of Focus scans will be kept.
 * @returns Focus files as ZipFile[].
 */
async function findFocusFiles(files: (File | FileExt)[])
  : Promise<{ remainingFiles: (File | FileExt)[], focusFiles: FocusZipFile[] }> {
  let remainingFiles = [...files];
  const filesToSkip: (File | FileExt)[] = [];
  const focusFiles: FocusZipFile[] = [];

  // Add Focus scans
  while (true) {
    const focusVersionFiles = remainingFiles.filter((file) => file.name === "Version");
    let focusVersionFile: File | undefined;
    for (const file of focusVersionFiles) {
      if (filesToSkip.includes(file)) {
        continue;
      }
      if (await isSupportedFocusScan(file)) {
        focusVersionFile = file;
        break;
      }
    }
    if (!focusVersionFile) {
      break;
    }

    const folderPrefix = getFolderPrefix(focusVersionFile.webkitRelativePath);
    const { folderFilesExt, folderFiles } = removeFolderPrefix(remainingFiles, folderPrefix);

    const focusFile = await getFocusZipFile(folderFilesExt);
    if (focusFile) {
      focusFiles.push(focusFile);
      const oldLength = remainingFiles.length;
      remainingFiles = remainingFiles.filter((file) => !folderFiles.includes(file));
      assert(folderFiles.length > 0 && remainingFiles.length < oldLength, "Logic error: No matching files found for the folder.");
    } else {
      // Remember this Version file so we don't try to process it again in the next loop.
      filesToSkip.push(focusVersionFile);
    }
  }

  return { remainingFiles, focusFiles };
}

/**
 * Remove the folder prefix from the webkitRelativePath of the files.
 * @param files The files to remove the prefix from.
 * @param folderPrefix the folder prefix to remove
 */
function removeFolderPrefix(files: (File | FileExt)[], folderPrefix: string): {
  folderFilesExt: FileExt[],
  folderFiles: (File | FileExt)[];
} {
  const folderFiles = files.filter((file) => file.webkitRelativePath.startsWith(folderPrefix));
  const folderFilesExt = folderFiles.map((file) => {
    const newRelativePath = file.webkitRelativePath.replace(folderPrefix, "");
    return FileExt.createFromFileWithNewPath(file, newRelativePath);
  });
  return { folderFilesExt, folderFiles };
}

/**
 * Extracts PhotogrammetryFilesInfo from the given files.
 * @param files Files, which are descendants of one root folder.
 * @param projectId
 * @returns Extracted PhotogrammetryFilesInfo, InvalidFilesInfo if no valid photos were found.
 */
function getPhotogrammetryFilesInfo(files: (File | FileExt)[], projectId: ProjectId)
  : PhotogrammetryFilesInfo | InvalidFilesInfo {

  function getInvalid(errorType: CheckFilesErrorToastType, folderName?: string, invalidFileName?: string): InvalidFilesInfo {
    return {
      elementType: UploadElementType.photogrammetry,
      folderName,
      filesToUpload: [],
      errorType,
      invalidFileName,
    };
  }
  
  const context: PhotogrammetryUploadTaskContext = {
    uploadElementType: UploadElementType.photogrammetry,
    scanId: generateGUID(),
    projectId,
  };

  const filesToUpload = files.filter((file) =>
    validateFile({
      file,
      allowedExtensions: ALLOWED_EXTENSIONS_PHOTOGRAMMETRY,
    }).isValid
  );
  if (filesToUpload.length === 0) {
    return getInvalid(CheckFilesErrorToastType.noFiles);
  }

  const folderName = getImportFolderName(filesToUpload);

  const tooLargeFiles = filesToUpload.filter((file) =>
    file.size > PHOTOGRAMMETRY_MAX_FILE_SIZE_IN_MB * FILE_SIZE_MULTIPLIER * FILE_SIZE_MULTIPLIER
  );
  if (tooLargeFiles.length > 0) {
    return getInvalid(CheckFilesErrorToastType.tooLargePhotos, folderName, tooLargeFiles[0].name);
  }
  const invalidZeroBytesFiles = filesToUpload.filter((file) => file.size === 0);
  if (invalidZeroBytesFiles.length > 0) {
    return getInvalid(CheckFilesErrorToastType.zeroBytesFiles, folderName, invalidZeroBytesFiles[0].name);
  }

  return {
    elementType: UploadElementType.photogrammetry,
    folderName,
    filesToUpload,
    context,
  };
}

/**
 * Extracts OrbisFilesInfo from the given files.
 * @param files Files, which are descendants of one root folder.
 * @returns Extracted OrbisFilesInfo, InvalidFilesInfo if no valid Orbis files were found.
 */
async function getOrbisFilesInfo(files: File[]): Promise<(OrbisFilesInfo | InvalidFilesInfo)[]> {

  function getInvalid(errorType: CheckFilesErrorToastType, folderName?: string, invalidFileName?: string): InvalidFilesInfo {
    return {
      elementType: UploadElementType.orbisScan,
      folderName,
      filesToUpload: [],
      errorType,
      invalidFileName,
    };
  }

  const results: (OrbisFilesInfo | InvalidFilesInfo)[] = [];
  const validFiles = files.filter((file) =>
    validateFile({ file, allowedExtensions: ALLOWED_EXTENSIONS_ORBIS }).isValid
  );

  if (validFiles.length === 0) {
    return [getInvalid(CheckFilesErrorToastType.noFiles)];
  }

  const scanIdMap: Set<string> = new Set();

  for (const orbisFile of validFiles) {
    let orbisMeta: OrbisUuids;
    try {
      orbisMeta = await extractUuidsFromFile(orbisFile);
    } catch {
      return [getInvalid(CheckFilesErrorToastType.invalidFormat, undefined, orbisFile.name)];
    }
    const lsDataV2 = createOrbisDummyLsDataV2(orbisFile, orbisMeta.datasetId);

    if (scanIdMap.has(orbisMeta.datasetId)) {
      results.push(
        getInvalid(CheckFilesErrorToastType.duplicateFiles, undefined, orbisFile.name)
      );
      continue;
    };

    scanIdMap.add(orbisMeta.datasetId);

    results.push({
      elementType: UploadElementType.orbisScan,
      folderName: orbisFile.name,
      filesToUpload: [orbisFile],
      lsDataV2,
      lsDataV2ZipFile: undefined,
      scansAlreadyUploaded: new Set(),
      hasDummyLsDataV2: true,
    });
  }

  return results;
}